import { FromToFilter, useFromToFilter, useRefreshCounter } from "./api.utils";
import {
  Alert,
  Beacon,
  BeaconPositionEvent,
  BeaconSensorEvent,
  BeaconTableSorting,
  DesiredBeaconConfig,
  GetBeaconConfigurationStateResponse,
  PagedResponse,
  UpdateEvent,
} from "./models";
import {
  ApiCall,
  ApiErrorResponse,
  ApiState,
  PagedApiState,
  UpdateResult,
  useApiState,
  useAuthorizedFetch,
  useGetAuthorized,
  usePagedApiState,
} from "./useApiState";

export function useBeacons(
  assignedToOrganization?: boolean,
  filterString?: string,
  onlyWithError?: boolean,
  sorting?: BeaconTableSorting,
  pageSize?: number
): PagedApiState<Beacon> {
  const refreshCounter = useRefreshCounter();

  let alreadyHasQueryParameter = false;

  let query = "";

  filterString = filterString?.trim();

  if (filterString) {
    const prefix = alreadyHasQueryParameter ? "&" : "?";
    query += prefix + "filter_by=" + filterString;
    alreadyHasQueryParameter = true;
  }

  if (onlyWithError !== undefined) {
    const prefix = alreadyHasQueryParameter ? "&" : "?";
    query += prefix + "has_errors=" + onlyWithError;
    alreadyHasQueryParameter = true;
  }

  if (sorting?.sortColumnId !== undefined) {
    const prefix = alreadyHasQueryParameter ? "&" : "?";
    query +=
      prefix + "sort=" + sorting.sortColumnId + "&order=" + sorting.sortOrderId;
    alreadyHasQueryParameter = true;
  }

  if (assignedToOrganization !== undefined) {
    const prefix = alreadyHasQueryParameter ? "&" : "?";
    query += prefix + "assigned_to_organization=" + assignedToOrganization;
    alreadyHasQueryParameter = true;
  }

  const get = useGetAuthorized<PagedResponse<Beacon>>("beacons" + query);
  const beacons = usePagedApiState(get, refreshCounter, pageSize);

  return beacons;
}

export function useErrorHistory(
  beaconSerial: string,
  fromToFilter: FromToFilter,
  take?: number
): PagedApiState<Alert> {
  const refreshCounter = useRefreshCounter();
  const url = `beacons/${beaconSerial}/history/alerts?`;
  const appendDateFilter = useFromToFilter(fromToFilter);

  const get = useGetAuthorized<PagedResponse<Alert>>(
    url,
    undefined,
    appendDateFilter
  );
  return usePagedApiState(get, refreshCounter);
}

export function useEventHistory(
  beaconSerial: string,
  fromToFilter: FromToFilter
): PagedApiState<UpdateEvent> {
  const refreshCounter = useRefreshCounter();
  const url = `beacons/${beaconSerial}/history/events?`;
  const appendDateFilter = useFromToFilter(fromToFilter);

  const get = useGetAuthorized<PagedResponse<UpdateEvent>>(
    url,
    undefined,
    appendDateFilter
  );
  return usePagedApiState(get, refreshCounter);
}

export function usePositionHistory(
  beaconSerial: string,
  fromToFilter: FromToFilter
): PagedApiState<BeaconPositionEvent> {
  const refreshCounter = useRefreshCounter();

  const url = `beacons/${beaconSerial}/history/position-telemetry?`;

  const appendDateFilter = useFromToFilter(fromToFilter);

  const get = useGetAuthorized<PagedResponse<BeaconPositionEvent>>(
    url,
    undefined,
    appendDateFilter
  );

  return usePagedApiState(get, refreshCounter);
}

export function useSensorHistory(
  beaconSerial: string,
  fromToFilter: FromToFilter
): PagedApiState<BeaconSensorEvent> {
  const refreshCounter = useRefreshCounter();
  const url = `beacons/${beaconSerial}/history/sensor-telemetry?`;
  const appendDateFilter = useFromToFilter(fromToFilter);

  const get = useGetAuthorized<PagedResponse<BeaconSensorEvent>>(
    url,
    undefined,
    appendDateFilter
  );

  return usePagedApiState(get, refreshCounter);
}

export function useActiveErrors(
  beaconSerial: string
): ApiState<Alert[] | undefined> {
  const refreshCounter = useRefreshCounter();

  const url = `beacons/${beaconSerial}/active-alerts`;

  const get = useGetAuthorized<Alert[]>(url);

  return useApiState(get, undefined, refreshCounter);
}

export function useBeacon(beaconId: string): ApiState<Beacon | undefined> {
  return useApiState(
    useGetAuthorized<Beacon>(`beacons/${beaconId}`),
    undefined
  );
}

export function useBeaconConfiguration(
  beaconSerial: string
): ApiState<GetBeaconConfigurationStateResponse | undefined> {
  return useApiState(
    useGetAuthorized<GetBeaconConfigurationStateResponse>(
      `beacons/${beaconSerial}/configuration`
    ),
    undefined
  );
}

export function useConfigureBeacon(): ApiCall<
  { beaconSerial: string; configuration: DesiredBeaconConfig },
  UpdateResult
> {
  const authorizedFetch = useAuthorizedFetch();
  return async (input: {
    beaconSerial: string;
    configuration: DesiredBeaconConfig;
  }) => {
    const result = await authorizedFetch(
      `beacons/${input.beaconSerial}/configuration`,
      { method: "put", body: JSON.stringify(input.configuration) }
    );

    if (!result.ok) {
      const errorResponse: ApiErrorResponse = await result.json();
      return {
        error: true,
        response: errorResponse,
      };
    } else {
      return {
        error: false,
        response: {
          statusCode: result.status,
          detail: { message: "Construction project updated successfully" },
        },
      };
    }
  };
}

export function useConfigureBeacons(): ApiCall<
  { beaconSerials: string[]; configuration: DesiredBeaconConfig },
  UpdateResult
> {
  const authorizedFetch = useAuthorizedFetch();
  return async (input: {
    beaconSerials: string[];
    configuration: DesiredBeaconConfig;
  }): Promise<UpdateResult> => {
    try {
      await Promise.all(
        input.beaconSerials.map(async (serial) => {
          const result = await authorizedFetch(
            `beacons/${serial}/configuration`,
            {
              method: "put",
              body: JSON.stringify(input.configuration),
            }
          );
          if (!result.ok) {
            throw new Error(
              `Failed to configure beacon with serial: ${serial}`
            );
          }
        })
      );
      return {
        error: false,
        response: {
          statusCode: 200,
          detail: { message: "All beacons configured successfully" },
        },
      };
    } catch (error) {
      return {
        error: true,
        response: {
          statusCode: 500,
          detail: {
            message: error instanceof Error ? error.message : "Unknown error",
          },
        },
      };
    }
  };
}
